import { useState } from 'react'
import { Loader2, Trash2 } from 'lucide-react'
import { Button } from '../../Components/ui/button'
import { Checkbox } from '../../Components/ui/checkbox'
import { Input } from '../../Components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../Components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../Components/ui/sheet'
import useMediaQuery from '../../hooks/useMediaQuery'
import env from '../../env/env'
import { toCurrency } from '../../utils/CurrencyUtil'
import { REDEMPTION_MODES, getBreakdownError, getLineError, sumLines } from './redeemBreakdown'

const MODE_COPY = {
  [REDEMPTION_MODES.DYNAMIC]: {
    title: 'Dynamic breakdown',
    description: 'This is the split Fabits calculated. It is sent with the redemption order.',
  },
  [REDEMPTION_MODES.CUSTOM]: {
    title: 'Custom breakdown',
    description:
      'Loaded from the redemption split. Change an amount or remove a fund before creating the order.',
  },
}

const FundMark = ({ amcCode, name }) => {
  const [failed, setFailed] = useState(false)
  if (!amcCode || failed) {
    return (
      <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--fb-accent-soft)] text-sm font-semibold text-[var(--fb-accent-text)]'>
        {(name || 'F').slice(0, 1)}
      </div>
    )
  }

  return (
    <img
      src={`${env.MF_LOGOS_URL}/${amcCode}.png`}
      alt=''
      className='h-11 w-11 shrink-0 rounded-xl object-cover'
      onError={() => setFailed(true)}
    />
  )
}

const FundRow = ({ line, editable, onAmountChange, onFullRedeemChange, onRemove }) => {
  const error = editable ? getLineError(line) : ''
  const fullRedeem = line.redemptionType === 'ALL_UNITS'

  return (
    <div className='flex items-start gap-3 border-b border-[var(--fb-border)] py-3 last:border-b-0'>
      <FundMark amcCode={line.amcCode} name={line.schemeName} />
      <div className='min-w-0 flex-1'>
        <p className='truncate font-semibold text-[var(--fb-text-strong)]'>{line.schemeName}</p>
        <p className='mt-0.5 text-xs text-[var(--fb-text-muted)]'>
          {line.assetClass ? `${line.assetClass} · ` : ''}
          Available ₹{toCurrency(line.currentValue)}
          {line.redemptionType && line.redemptionType !== 'VALUE'
            ? ` · ${line.redemptionType === 'ALL_UNITS' ? 'Full redemption' : line.redemptionType}`
            : ''}
        </p>
        {editable ? (
          <div className='mt-2 flex flex-wrap items-center gap-3'>
            <div className='w-full max-w-[180px]'>
              <Input
                startAdornment='₹'
                inputMode='numeric'
                aria-label={`Amount for ${line.schemeName}`}
                value={line.amount ? toCurrency(line.amount) : ''}
                placeholder='0'
                disabled={fullRedeem}
                onChange={(event) => onAmountChange(line.id, event.target.value)}
                className='h-10 rounded-xl border-[var(--fb-border)] bg-[var(--fb-surface-muted)]'
              />
            </div>
            <label className='inline-flex items-center gap-2 text-xs font-semibold text-[var(--fb-text)]'>
              <Checkbox
                checked={fullRedeem}
                aria-label={`Full redeem ${line.schemeName}`}
                onCheckedChange={(checked) => onFullRedeemChange(line.id, checked === true)}
              />
              Full redeem
            </label>
          </div>
        ) : null}
        {error ? <p className='mt-1 text-xs font-medium text-[var(--fb-danger)]'>{error}</p> : null}
      </div>
      <div className='flex shrink-0 items-center gap-2'>
        {!editable ? (
          <p className='text-sm font-semibold text-[var(--fb-text-strong)]'>
            ₹{toCurrency(line.amount)}
          </p>
        ) : null}
        {editable ? (
          <button
            type='button'
            aria-label={`Remove ${line.schemeName}`}
            className='inline-flex h-9 w-9 items-center justify-center rounded-full text-[var(--fb-text-muted)] hover:bg-[var(--fb-danger-bg)] hover:text-[var(--fb-danger)]'
            onClick={() => onRemove(line.id)}
          >
            <Trash2 className='h-4 w-4' />
          </button>
        ) : null}
      </div>
    </div>
  )
}

const BreakdownBody = ({
  mode,
  lines,
  loading,
  fetchError,
  availableAmount,
  submitting,
  otpSubmitting,
  onAmountChange,
  onFullRedeemChange,
  onRemove,
  onRestore,
  onConfirm,
  onCompleteWithOtp,
  onRetry,
}) => {
  const copy = MODE_COPY[mode] || MODE_COPY[REDEMPTION_MODES.DYNAMIC]
  const editable = mode === REDEMPTION_MODES.CUSTOM
  const total = sumLines(lines)
  const validationError = !loading && !fetchError ? getBreakdownError(lines, availableAmount) : ''
  const isBusy = submitting || otpSubmitting
  const canConfirm = !loading && !fetchError && !validationError && !isBusy && total > 0

  return (
    <div className='flex flex-col'>
      <div className='max-h-[48vh] overflow-y-auto pr-1'>
        {loading ? (
          <div className='flex flex-col items-center justify-center gap-2 py-16 text-sm text-[var(--fb-text-muted)]'>
            <Loader2 className='h-6 w-6 animate-spin text-[var(--fb-accent)]' />
            Fetching the redemption split
          </div>
        ) : null}

        {!loading && fetchError ? (
          <div className='rounded-2xl border border-[var(--fb-danger-border)] bg-[var(--fb-danger-bg)] px-4 py-5 text-sm text-[var(--fb-danger)]'>
            <p>{fetchError}</p>
            <Button
              type='button'
              variant='outline'
              className='mt-4 h-10 rounded-xl'
              onClick={onRetry}
            >
              Try again
            </Button>
          </div>
        ) : null}

        {!loading && !fetchError && lines.length === 0 ? (
          <div className='rounded-2xl border border-dashed border-[var(--fb-border)] bg-[var(--fb-surface-muted)] px-4 py-8 text-center'>
            <p className='font-semibold text-[var(--fb-text-strong)]'>No funds in this split</p>
            <p className='mt-1 text-sm text-[var(--fb-text-muted)]'>
              Restore the funds to build a redemption from the current holdings.
            </p>
            {editable ? (
              <Button
                type='button'
                variant='outline'
                className='mt-4 h-10 rounded-xl'
                onClick={onRestore}
              >
                Restore funds
              </Button>
            ) : null}
          </div>
        ) : null}

        {!loading && !fetchError && lines.length > 0 ? (
          <div>
            {lines.map((line) => (
              <FundRow
                key={line.id}
                line={line}
                editable={editable}
                onAmountChange={onAmountChange}
                onFullRedeemChange={onFullRedeemChange}
                onRemove={onRemove}
              />
            ))}
          </div>
        ) : null}
      </div>

      <div className='mt-4 border-t border-[var(--fb-border)] pt-4'>
        <div className='flex items-end justify-between gap-3'>
          <div>
            <p className='text-xs font-medium uppercase tracking-[0.08em] text-[var(--fb-text-muted)]'>
              Total redemption
            </p>
            <p className='font-DMSans text-2xl font-bold tracking-tight text-[var(--fb-text-strong)]'>
              ₹{toCurrency(total)}
            </p>
          </div>
          <p className='text-right text-xs text-[var(--fb-text-muted)]'>
            Available
            <br />₹{toCurrency(Math.floor(availableAmount || 0))}
          </p>
        </div>
        {validationError ? (
          <p className='mt-2 text-sm font-medium text-[var(--fb-danger)]'>{validationError}</p>
        ) : otpSubmitting ? (
          <p className='mt-2 text-sm font-medium text-[var(--fb-accent-text)]'>
            Sending OTP… keep this open until verification starts.
          </p>
        ) : submitting ? (
          <p className='mt-2 text-sm font-medium text-[var(--fb-accent-text)]'>
            Creating the Action Center redemption…
          </p>
        ) : (
          <p className='mt-2 text-xs text-[var(--fb-text-muted)]'>{copy.description}</p>
        )}
        <div className='mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2'>
          <Button
            type='button'
            variant='outline'
            className='h-12 w-full rounded-xl border-[var(--fb-accent)] text-base font-semibold text-[var(--fb-accent-text)] hover:bg-[var(--fb-accent-soft)]'
            loading={otpSubmitting}
            disabled={!canConfirm}
            onClick={onCompleteWithOtp}
          >
            {otpSubmitting ? 'Sending OTP…' : 'Complete with OTP'}
          </Button>
          <Button
            type='button'
            className='h-12 w-full rounded-xl bg-[var(--fb-accent)] text-base font-semibold text-white hover:bg-[var(--fb-accent-hover)]'
            loading={submitting}
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {submitting ? 'Pushing…' : 'Push to Action Center'}
          </Button>
        </div>
      </div>
    </div>
  )
}

const RedeemBreakdownSheet = (props) => {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const copy = MODE_COPY[props.mode] || MODE_COPY[REDEMPTION_MODES.DYNAMIC]

  if (!props.open) return null

  if (isDesktop) {
    return (
      <Dialog open={props.open} onOpenChange={props.onOpenChange}>
        <DialogContent showClose className='max-h-[85vh] w-[80vw] sm:max-w-[80vw] sm:rounded-3xl'>
          <DialogHeader>
            <DialogTitle className='font-DMSans text-xl text-[var(--fb-text-strong)]'>
              {copy.title}
            </DialogTitle>
            <DialogDescription className='text-sm text-[var(--fb-text-muted)]'>
              Review the funds included in this redemption.
            </DialogDescription>
          </DialogHeader>
          <BreakdownBody {...props} />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Sheet open={props.open} onOpenChange={props.onOpenChange}>
      <SheetContent side='bottom' className='max-h-[88vh] rounded-t-3xl px-4 pb-6 pt-5'>
        <SheetHeader className='mb-2 text-left'>
          <SheetTitle className='font-DMSans text-lg text-[var(--fb-text-strong)]'>
            {copy.title}
          </SheetTitle>
          <SheetDescription className='text-sm text-[var(--fb-text-muted)]'>
            Review the funds included in this redemption.
          </SheetDescription>
        </SheetHeader>
        <BreakdownBody {...props} />
      </SheetContent>
    </Sheet>
  )
}

export default RedeemBreakdownSheet
